/* eslint-disable no-nested-ternary */
import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";

import DataTable from "components/Widgets/DataTable";
import AmountDetails from "components/Screens/AmountDetails";
import ProfleWithIcon from "components/Widgets/ProfileWithIcon";
import CreateOneTimeChargeButton from "components/common/CreateOneTimeChargeButton";
import UpdatePaymentStatusButton from "components/common/UpdatePaymentStatusButton";
import UpdatePaymentStatusModal from "components/common/UpdatePaymentStatusModal";
import ManualPaymentUpdate from "components/common/ManualPaymentUpdate";

import PageHeader from "components/Screens/PageHeader";
import AvatarIcon from "components/Icons/AvatarIcon";
import { usePermissions } from "components/Hook";
import { hasPermission } from "hooks/usePermission";
import {
  convertDate,
  getCurrencySymbol,
  currencyFormat,
  getValue,
  getPaymentStatus,
} from "components/Screens/CommonUtils";
import { tabHeadings, noticeTableHeaders } from "components/Screens/Customers/CustomerDetials/constants";
import EditCreditModal from "components/Screens/EditCreditModal";

import { baseUrl } from "util/APIBaseUrl";
import { formatFinanceAdjustmentAmount } from "util/financeAdjustment";
import { getUserAdditionalInfo } from "selectors/user";
import { getPaymentStatusText, getStatusColor } from "components/Screens/Customers/utils";
import { updatePaymentStatus, downloadPayrollDocument } from "services/Admin/Customer";
import { showSnackbar } from "store/actions/Utility";
import { AlertType } from "components/common/Snackbar";

import { CUSTOMER_DETAILS_ROUTE } from "constants/routes";
import { getPaymentMethodLabel } from "constants/paymentMethods";

import { GetNoticeBoard } from "store/actions/DashBoard/GetNoticeBoard";

import { BsCreditCard2Back } from "react-icons/bs";

// [PRD-3746] Show the calendar date of an ISO paidOn as is, without a timezone shift.
const convertPaidOnDate = (paidOn) => convertDate(/^\d{4}-\d{2}-\d{2}/.exec(paidOn || "")?.[0] || paidOn);

function PaymentBreakDown(props) {
  const { customizedInvoicing } = useFlags();

  const {
    paymentDetail,
    selectedOptions,
    selectedFiltersList,
    resetPage,
    customerPayment,
    openDetailsPage,
    handlePagination,
    handleSelectedRow,
    navigatedFrom,
    salariesPage,
    toolBar,
    filterButton,
    SearchEmployee,
    title,
    handleRequestSortBy,
    ExportButton,
    datePicker,
    onNavigateToPaymentHistory,
    onCreditUpdate,
    paymentHistoryData,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { loggedInUserType } = usePermissions();
  const showFinancials = hasPermission("payroll.read");

  const canCharge = hasPermission("payments.charge");
  const customerDetails = useSelector((state) => state.GetCustomerDetails?.customerDetails);
  const isChurnedCustomer = customerDetails?.payload?.status === "churned";
  const canConfigure = hasPermission("payments.configure");

  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState({});
  const [tableRows, setTableRows] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTab, setSelectedTab] = useState("Overview");
  const [noticesTableRows, setnoticesTableRows] = useState([]);
  const [editCreditModalOpen, setEditCreditModalOpen] = useState(false);
  const [updatePaymentStatusModalOpen, setUpdatePaymentStatusModalOpen] = useState(false);
  const [isUpdatingPaymentStatus, setIsUpdatingPaymentStatus] = useState(false);

  const { ok: noticeBoardOk, payload: noticeBoardPayload } =
    useSelector((state) => state.GetNoticeBoard.noticeBoard) || {};

  const {
    customerId: customerIdFromRedux,
    totalPayrollReceived,
    totalPayroll: userInfoTotalPayroll,
  } = useSelector(getUserAdditionalInfo);

  const {
    rows: customerPaymentRows,
    totalPayroll: customerPaymentTotalPayroll,
    totalBonus: customerPaymentTotalBonus,
    totalEmployees: customerPaymentTotalEmployees,
    creditCardSurcharge: customerPaymentCardSurcharge,
  } = customerPayment || {};

  const {
    totalPayroll: paymentDetailTotalPayroll,
    totalBonus: paymentDetailTotalBonus,
    totalEmployees: paymentDetailTotalEmployees,
    status: paymentDetailStatus,
    paidOn: paymentDetailPaidOn,
    paymentDate: paymentDetailPaymentDate,
    billedOn: paymentDetailBilledOn,
    creditCardSurcharge: paymentDetailCardSurcharge,
    customerId: customerIdFromPaymentDetail,
    financeAdjustment: paymentDetailFinanceAdjustment,
  } = paymentDetail || {};

  // totalPayroll from the server already includes this adjustment; the row is informational only.
  const financeAdjustmentDetail = !salariesPage &&
    paymentDetailFinanceAdjustment && {
      title: "Billing adjustment",
      amount: formatFinanceAdjustmentAmount(paymentDetailFinanceAdjustment.amount),
      subtext: paymentDetailFinanceAdjustment.reason,
    };

  const customerId = customerIdFromPaymentDetail || params.customerId || customerIdFromRedux;

  const handleRowsPerPage = (pagination) => {
    handlePagination(pagination);
  };

  const handleRequestNoticesSortBy = (request) => {
    setSortBy(request);
  };

  const handleNoticesRowsPerPage = ({ rowsPerPage: noticesRowsPerPage, page: noticesPage }) => {
    setRowsPerPage(noticesRowsPerPage);
    setPage(noticesPage);
  };

  const handleEdit = () => {
    setEditCreditModalOpen(true);
  };

  const selectPageHeading = (opt) => {
    setSelectedTab(opt);
  };

  const formatCurrency = (value) => {
    return getCurrencySymbol("en-US", "USD", currencyFormat(getValue(value)));
  };

  const getAmountDetails = () => {
    if (!customizedInvoicing) {
      return [
        {
          title: salariesPage ? "Total Payroll(All Customers)" : "Total Payroll",
          amount: getCurrencySymbol(
            "en-US",
            "USD",
            currencyFormat(getValue(salariesPage ? customerPayment.totalPayroll : paymentDetail?.totalPayroll))
          ),
        },
        financeAdjustmentDetail,
        {
          title: "Total Bonus",
          amount: getCurrencySymbol(
            "en-US",
            "USD",
            currencyFormat(getValue(salariesPage ? customerPayment.totalBonus : paymentDetail?.totalBonus))
          ),
        },
        {
          title: "Employees",
          amount: getValue(salariesPage ? customerPayment.totalEmployees : paymentDetail?.totalEmployees),
        },
        !salariesPage && {
          title:
            navigatedFrom == "paymentDetails"
              ? paymentDetail?.status == "paid"
                ? "Paid On"
                : "Payment Date"
              : "Payment Date",

          amount:
            navigatedFrom == "paymentDetails"
              ? paymentDetail?.paymentStatus?.status == "paid"
                ? convertPaidOnDate(paymentDetail?.paidOn)
                : convertDate(paymentDetail?.paymentDate)
              : convertDate(paymentDetail?.billedOn),
        },
      ];
    }

    return [
      {
        title: "Total Payroll",
        amount: formatCurrency(salariesPage ? customerPaymentTotalPayroll : paymentDetailTotalPayroll),
        showDivider: true,
      },
      financeAdjustmentDetail,
      {
        title: "Total Bonus",
        amount: formatCurrency(salariesPage ? customerPaymentTotalBonus : paymentDetailTotalBonus),
      },

      salariesPage === false && {
        title: "Total Credit",
        amount: getCurrencySymbol("en-US", "USD", currencyFormat(getValue(creditAmount))),
        ...(canCharge && {
          editIcon: true,
          onEditClick: handleEdit,
        }),
      },

      salariesPage !== false && {
        title: "Total Credit Adjusted",
        amount: getCurrencySymbol("en-US", "USD", currencyFormat(getValue(startingBalance?.replace("-", "")))),
      },
      {
        title: "Card Surcharge",
        amount: formatCurrency(customerPaymentCardSurcharge || paymentDetailCardSurcharge),
        showDivider: true,
      },
      {
        title: "Employees",
        amount: getValue(salariesPage ? customerPaymentTotalEmployees : paymentDetailTotalEmployees),
      },
      !salariesPage && {
        title:
          navigatedFrom == "paymentDetails"
            ? paymentDetailStatus == "paid"
              ? "Paid on"
              : "Payment Date"
            : "Payment Date",
        amount:
          navigatedFrom == "paymentDetails"
            ? paymentDetailStatus == "paid"
              ? convertPaidOnDate(paymentDetailPaidOn)
              : convertDate(paymentDetailPaymentDate)
            : convertDate(paymentDetailBilledOn),
      },
      salariesPage !== false && {
        title: "Payment Status",
        amountTextColor: getStatusColor(paymentDetailStatus),
        amount: getPaymentStatusText(navigatedFrom, paymentDetailStatus),
      },
    ];
  };

  useEffect(() => {
    if (noticeBoardOk) {
      const formattedNotices = noticeBoardPayload?.rows?.map((notice) => {
        const { id: noticeId, customer: noticeCustomer, notice: noticeText, date: noticeDate } = notice;

        return {
          id: noticeId,
          customerName: (
            <Typography
              variant="h3"
              onClick={() => navigate(CUSTOMER_DETAILS_ROUTE.replace(":customerId", noticeCustomer?.id))}
              style={{ cursor: "pointer" }}
            >
              {noticeCustomer.name}
            </Typography>
          ),
          noticeText,
          date: convertDate(noticeDate),
        };
      });

      setnoticesTableRows(formattedNotices || []);
    }
  }, [noticeBoardOk]);

  const handleViewCharge = () => {
    if (onNavigateToPaymentHistory) {
      onNavigateToPaymentHistory();
    }
  };

  const handleUpdatePaymentStatus = () => {
    setUpdatePaymentStatusModalOpen(true);
  };

  const handlePaymentStatusSubmit = async (payload) => {
    try {
      setIsUpdatingPaymentStatus(true);
      await updatePaymentStatus(customerId, paymentDetail.customerPayrollId, payload);

      dispatch(
        showSnackbar({
          type: AlertType.success,
          message: "Payment status updated successfully",
          dispatch,
        })
      );

      if (onCreditUpdate) {
        setTimeout(() => onCreditUpdate(), 2000);
      }

      setUpdatePaymentStatusModalOpen(false);
    } catch (error) {
      const errorMessage = error?.message || "Failed to update payment status";
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: errorMessage,
          dispatch,
        })
      );
      // Tells the modal to stay open so the typed values are kept.
      return false;
    } finally {
      setIsUpdatingPaymentStatus(false);
    }
  };

  const shouldShowUpdatePaymentStatusButton = () => {
    if (salariesPage || !paymentDetail) {
      return false;
    }

    const paymentType = paymentHistoryData?.paymentType?.toLowerCase();
    const status = paymentDetail?.status?.toLowerCase();
    const updatedBy = paymentDetail?.updatedBy?.toLowerCase();
    const paymentDate = new Date(paymentDetail?.paymentDate);
    const today = new Date();

    if (paymentType !== "monthly") {
      return false;
    }

    if (paymentDate >= today) {
      return false;
    }

    // Once the invoice is settled/closed, never show the button — even if the
    // row's cached status (paymentHistoryData) is stale from before the payment
    // completed. paymentDetail.status is the freshest source of truth.
    if (status === "paid" || status === "void") {
      return false;
    }

    // [PRD-3741] "failed" is a manually failed payment (monolith Status and BS adapter); finance can record it again.
    if (status === "un-paid" || status === "payment_failed" || status === "failed" || status === "processing") {
      return true;
    }

    if (status === "pending" && updatedBy === "admin") {
      return true;
    }

    const bsInvoiceStatus = paymentHistoryData?.status?.toLowerCase();
    if (bsInvoiceStatus === "finalized") {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const Data = [];

    if (customerPayment && customerPaymentRows?.length) {
      customerPaymentRows.forEach((employee) => {
        const {
          employeeId,
          pictureVersion,
          firstName,
          lastName,
          jobTitle,
          companyName: employeeCompanyName,
          costToCompany: employeeCostToCompany,
          platformFee: employeePlatformFee,
          grossSalary: employeeGrossSalary,
          tax,
          netSalary,
          incrementDetails,
          bonus: employeeBonus,
          status: employeeStatus,
          failure: employeeFailure,
          startDate: employeeStartDate,
          country: employeeCountry,
          paymentMethod: employeePaymentMethod,
        } = employee;

        const { bonus: incrementDetailsBonus } = incrementDetails || {};

        Data.push({
          id: employeeId,
          Profile_Picture: (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                ml: 0,
                pl: 0,
                ml: 0,
                pl: 0
              }}>
              <AvatarIcon
                shape="circle"
                size={{
                  width: "8vh",
                  height: "8vh",
                  borderRadius: "50%",
                }}
                source={`${baseUrl}/employee/${employeeId}/logo?id=${pictureVersion}&variant=thumbnail`}
              />
            </Box>
          ),
          employeeName: (
            <ProfleWithIcon
              type="textOnly"
              data={{
                name: `${getValue(firstName)} ${getValue(lastName)}`,
                role: getValue(jobTitle),
              }}
            />
          ),
          companyName: <Typography variant="h3">{getValue(employeeCompanyName)}</Typography>,
          paymentMethod: <Typography variant="h3">{getPaymentMethodLabel(employeePaymentMethod)}</Typography>,
          costToCompany: (
            <Typography variant="h3">
              {getCurrencySymbol("en-US", "USD", currencyFormat(getValue(employeeCostToCompany)))}
            </Typography>
          ),
          platformFee: (
            <Typography variant="h3">
              {getCurrencySymbol("en-US", "USD", currencyFormat(getValue(employeePlatformFee)))}
            </Typography>
          ),
          grossPay: (
            <Typography variant="h3">
              {getCurrencySymbol("en-US", "USD", currencyFormat(getValue(employeeGrossSalary)))}
            </Typography>
          ),
          taxes: (
            <Typography variant="h3">{getCurrencySymbol("en-US", "USD", currencyFormat(getValue(tax)))}</Typography>
          ),
          netPay: (
            <Typography variant="h3">
              {getCurrencySymbol("en-US", "USD", currencyFormat(getValue(netSalary)))}
            </Typography>
          ),
          bonus: (
            <Typography variant="h3">
              {getCurrencySymbol(
                "en-US",
                "USD",
                currencyFormat(getValue(incrementDetailsBonus || employeeBonus || "-"))
              )}
            </Typography>
          ),
          status: <Typography variant="h3">{getValue(getPaymentStatus(employeeStatus, employeeFailure))}</Typography>,
          date: <Typography variant="h3">{convertDate(employeeStartDate)}</Typography>,
          country: <Typography variant="h3">{getValue(employeeCountry)}</Typography>,
        });
      });
    }

    setTableRows(Data);
  }, [customerPayment]);

  const { creditAmount, startingBalance } = paymentDetail;

  useEffect(() => {
    if (selectedTab === "Important Notices") {
      if (customerId) {
        let request;
        if (loggedInUserType == "edge") {
          request = "customers/noticeboard";
        } else if (loggedInUserType == "customer") {
          request = `customer/${customerId}/noticeboard`;
        }
        dispatch(
          GetNoticeBoard({
            request,
            rowsPerPage,
            page,
            sortBy: {
              field: sortBy.orderBy,
              direction: sortBy.order,
            },
          })
        );
      }
    }
  }, [selectedTab, customerId, loggedInUserType, rowsPerPage, page, sortBy]);

  useLayoutEffect(() => {
    // TODO - move these cells logic to a separate constants file and import them from there
    let tableheadCells = [
      {
        id: "Profile_Picture",
        mobileView: true,
        label: "",
        align: "right",
        width: "20px,",
        sort: false,
      },
      {
        id: "employeeName",
        numeric: false,
        wrapCell: true,
        width: "230px",
        disablePadding: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Employee Name
          </Typography>
        ),
      },
      {
        id: "costToCompany",
        numeric: false,
        disablePadding: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Cost To Company
          </Typography>
        ),
      },
      {
        id: "platformFee",
        numeric: false,
        disablePadding: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Platform Fee
          </Typography>
        ),
      },
      {
        id: "grossPay",
        numeric: false,
        disablePadding: false,
        sort: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Gross Pay
          </Typography>
        ),
      },

      {
        id: "taxes",
        numeric: false,
        disablePadding: false,
        sort: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Taxes
          </Typography>
        ),
      },
      {
        id: "netPay",
        numeric: false,
        disablePadding: false,
        sort: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Net Pay
          </Typography>
        ),
      },
      {
        id: "bonus",
        numeric: false,
        disablePadding: false,
        label: (
          <Typography variant="h5" sx={{
            fontSize: "14px"
          }}>
            Bonus
          </Typography>
        ),
      },
    ];

    const companyName = {
      id: "companyName",
      numeric: false,
      disablePadding: false,
      wrapCell: true,
      width: "230px",
      label: (
        <Typography variant="h5" sx={{
          fontSize: "14px"
        }}>
          Company Name
        </Typography>
      ),
    };

    const status = {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: (
        <Typography variant="h5" sx={{
          fontSize: "14px"
        }}>
          Status
        </Typography>
      ),
    };

    const startDate = {
      id: "date",
      numeric: true,
      disablePadding: false,
      label: (
        <Typography variant="h5" sx={{
          fontSize: "14px"
        }}>
          Start Date
        </Typography>
      ),
    };

    const country = {
      id: "country",
      numeric: false,
      disablePadding: false,
      label: (
        <Typography variant="h5" sx={{
          fontSize: "14px"
        }}>
          Employee Country
        </Typography>
      ),
    };

    const paymentMethod = {
      id: "paymentMethod",
      numeric: false,
      disablePadding: false,
      sort: false,
      label: (
        <Typography variant="h5" sx={{
          fontSize: "14px"
        }}>
          Customer Payment Method
        </Typography>
      ),
    };

    if (salariesPage) {
      tableheadCells.splice(2, 0, companyName);
      tableheadCells.splice(3, 0, country);
      tableheadCells.splice(10, 0, status);
    }

    if (customizedInvoicing) {
      tableheadCells.splice(5, 1);
      tableheadCells = [...tableheadCells, startDate];
    } else {
      tableheadCells.splice(8, 0, startDate);
    }

    if (!showFinancials) {
      const FINANCIAL_COLUMN_IDS = ["costToCompany", "platformFee", "grossPay", "taxes", "netPay", "bonus"];
      tableheadCells = tableheadCells.filter((cell) => !FINANCIAL_COLUMN_IDS.includes(cell.id));
    }

    if (salariesPage) {
      const costToCompanyIndex = tableheadCells.findIndex((cell) => cell.id === "costToCompany");
      if (costToCompanyIndex !== -1) {
        tableheadCells.splice(costToCompanyIndex, 0, paymentMethod);
      }
    }

    setTableHeader(tableheadCells);
  }, [showFinancials]);

  return (
    <Grid container direction="row" rowSpacing={3} columnSpacing={3}>
      {salariesPage && (
        <PageHeader pageHeadings={tabHeadings} selectPageHeading={selectPageHeading} selectedOption={selectedTab} />
      )}
      {selectedTab == "Overview" && (
        <Grid
          container
          direction="row"
          rowSpacing={3}
          columnSpacing={3}
          size={{
            lg: 12,
            md: 12,
            sm: 12,
            xs: 12
          }}>
          {salariesPage && (
            <Grid
              size={{
                lg: salariesPage ? 6 : 12,
                md: 12,
                sm: 12,
                xs: 12
              }}>
              <AmountDetails
                details={[
                  {
                    title: "Payment Recieved",
                    amount: getCurrencySymbol("en-US", "USD", currencyFormat(getValue(totalPayrollReceived))),
                  },
                  {
                    title: "Total Payroll(Active Customers)",
                    amount: getCurrencySymbol("en-US", "USD", currencyFormat(getValue(userInfoTotalPayroll))),

                    icon: <BsCreditCard2Back color="var(--color-primitives-primary-900)" fontSize="30px" />,
                  },
                ]}
              />
            </Grid>
          )}

          <Grid
            size={{
              lg: salariesPage ? 6 : 12,
              md: 12,
              sm: 12,
              xs: 12
            }}>
            <AmountDetails
              details={getAmountDetails()}
              {...(customizedInvoicing &&
                navigatedFrom !== "paymentDetails" &&
                canCharge &&
                !isChurnedCustomer && {
                  action: <CreateOneTimeChargeButton onViewCharge={handleViewCharge} />,
                  actionPosition: "right",
                  salariesPage,
                })}
              {...(shouldShowUpdatePaymentStatusButton() &&
                canConfigure && {
                  action: <UpdatePaymentStatusButton onUpdate={handleUpdatePaymentStatus} />,
                  actionPosition: "right",
                })}
            />
          </Grid>

          {!salariesPage && paymentDetail?.updatedBy?.toLowerCase() === "admin" && (
            <Grid size={12}>
              <ManualPaymentUpdate
                audit={paymentDetail?.latestAudit}
                documents={paymentDetail?.documents}
                history={paymentDetail?.manualPaymentHistory}
                paymentMethod={paymentDetail?.paymentMethod}
                referenceNumber={paymentDetail?.referenceNumber}
                onDownloadProof={async (stageDocuments) => {
                  try {
                    // When a stage's documents are passed, download only those;
                    // otherwise (legacy single-summary) download all.
                    const s3Keys = Array.isArray(stageDocuments)
                      ? stageDocuments.map((doc) => doc.s3Key).filter(Boolean)
                      : undefined;
                    await downloadPayrollDocument(customerId, paymentDetail.customerPayrollId, s3Keys);
                  } catch (error) {
                    dispatch(
                      showSnackbar({
                        type: AlertType.error,
                        message: error.message || "Failed to download document",
                        dispatch,
                      })
                    );
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
      {selectedTab == "Important Notices" && salariesPage && (
        <Grid
          size={{
            lg: 12,
            md: 12,
            sm: 12,
            xs: 12
          }}>
          <DataTable
            rowsData={noticesTableRows}
            tableheadCells={noticeTableHeaders}
            title={`Important Notices (${noticeBoardPayload?.count || 0})`}
            totalCount={noticeBoardPayload?.count || 0}
            handleRowsPerPage={handleNoticesRowsPerPage}
            handleRequestSortBy={handleRequestNoticesSortBy}
          />
        </Grid>
      )}
      {selectedFiltersList || null}
      {selectedTab == "Overview" && (
        <Grid size={12}>
          <DataTable
            rowsData={tableRows}
            resetPage={resetPage}
            totalCount={customerPayment?.count || 0}
            tableheadCells={tableHeader}
            title={title || null}
            toolBar={toolBar || false}
            filterButton={filterButton || null}
            datePicker={datePicker || null}
            SearchEmployee={SearchEmployee || null}
            subTitle={null}
            selectedOptions={selectedOptions}
            openDetailsPage={openDetailsPage}
            slectedRowNavigation="/main/details"
            handleRowsPerPage={handleRowsPerPage}
            handleSelectedRow={handleSelectedRow}
            handleRequestSortBy={handleRequestSortBy || null}
            sortByName="employeeName"
            AddNewRowButton={ExportButton || null}
          />
        </Grid>
      )}
      {customizedInvoicing ? (
        <EditCreditModal
          setOpen={setEditCreditModalOpen}
          open={editCreditModalOpen}
          onClose={() => setEditCreditModalOpen(false)}
          customerId={customerId}
          previousCreditAmount={creditAmount}
          onCreditUpdated={onCreditUpdate}
        />
      ) : null}
      <UpdatePaymentStatusModal
        open={updatePaymentStatusModalOpen}
        onClose={() => setUpdatePaymentStatusModalOpen(false)}
        onSubmit={handlePaymentStatusSubmit}
        currentStatus={paymentDetailStatus}
        customerId={customerId}
        paymentData={paymentDetail}
        paymentHistoryData={paymentHistoryData}
        isLoading={isUpdatingPaymentStatus}
      />
    </Grid>
  );
}

export default PaymentBreakDown;
